import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../../components/ToastContext';
import { dayAndClock } from '../../../lib/when';
import { planRun } from './runPlan';
import { loadRunInputs, overridesFrom, saveRun } from './runData';
import { keyOf } from './conversions';
import AeFileSection from './AeFileSection';
import Pill from '../../../components/Pill';
import { Figure, Figures } from '../../../components/Figures';
import { Section } from './panelParts';
import { findAccountEdgeCandidates } from './lineFix';
import { linkClaimSoftToAe } from './runPick';

/*
  WORKING A RUN, inside the job panel once a job is picked up. Ordering stage 4,
  12 October 2026, on his word: "Yes, add them and build it".

  The plan is worked out by `planRun` (runPlan.js) from the morning reports and
  his rules, and shown line by line with why. **He has the last word** on every
  item number, quantity and route, his words: *"the process can do that calcs
  and make the suggestions but i need final say on what the qty and part number
  would be."* Every change works the plan out again straight away, so raising a
  line shows each vendor's gap to its minimum closing. **Save the run** keeps
  it on the job for the Account Edge file (stage 5).
*/

/* The four things to do with a line, as the buttons say them. Their colours,
   in the stylesheet's .run-seg, are the brand's meanings (guide 4.4): on order
   blue, Austin has it in stock green, nothing grey, waiting on him gold. */
const ROUTE_WORD = { order: 'Order', transfer: 'Austin', skip: 'Skip', hold: 'Hold' };
const money = n => '$' + Number(n || 0).toFixed(2);

/* The sum, with its labels above the numbers and no brackets, his words of 12
   October 2026, so both signs are minus. */
function NeedSum({ par, onHand, onOrder }) {
  const need = Math.max(0, par - onHand - onOrder);
  return (
    <table className="run-sum">
      <thead><tr><th>Par</th><th /><th>On hand</th><th /><th>On order</th><th /><th>True need</th></tr></thead>
      <tbody><tr>
        <td>{par}</td><td className="op">-</td><td>{onHand}</td><td className="op">-</td><td>{onOrder}</td><td className="op">=</td><td className="res">{need}</td>
      </tr></tbody>
    </table>
  );
}

/*
  **Austin's on hand, min level and excess**, his word of 12 October 2026: *"I
  want to see both the austin excess, austin on hand and austin min level."*
  Labelled above the numbers, like the stock sum. Excess is on hand above the
  min level, his rule the same day.
*/
function AustinCell({ a }) {
  if (a === null) return <span className="run-sub" style={{ marginTop: 0 }}>No Excess Calc loaded, or no Account Edge number.</span>;
  if (a === 'none') return <span className="run-sub" style={{ marginTop: 0 }}>None on hand in Austin.</span>;
  return (
    <table className="run-sum">
      <thead><tr><th>On hand</th><th /><th>Min level</th><th /><th>Excess</th></tr></thead>
      <tbody><tr>
        <td>{a.onHand}</td><td className="op">-</td><td>{a.minLevel}</td><td className="op">=</td><td className="res">{a.spare}</td>
      </tr></tbody>
    </table>
  );
}

/*
  **The Stock column, always open**, his word of 12 October 2026: the family
  with each number's par, on hand and on order and the family total, then the
  sum; or, for an item in no family, the same sum for the item itself.
*/
function StockCell({ p, own }) {
  if (p.audit) {
    if (!p.audit.each.length) return <span className="run-sub" style={{ marginTop: 0 }}>No number in this family is on the Stock Status here.</span>;
    return (
      <div className="run-stock">
        {p.audit.each.length > 1 && (
          <table className="run-fam">
            <thead><tr><th>Family</th><th className="n">Par</th><th className="n">On hand</th><th className="n">On order</th></tr></thead>
            <tbody>
              {p.audit.each.map(e => (
                <tr key={e.item}><td className="rpnum">{e.item}</td><td className="n">{e.par}</td><td className="n">{e.onHand}</td><td className="n">{e.onOrder}</td></tr>
              ))}
            </tbody>
          </table>
        )}
        <NeedSum par={p.audit.par} onHand={p.audit.onHand} onOrder={p.audit.onOrder} />
      </div>
    );
  }
  if (own === undefined) return <span className="run-sub" style={{ marginTop: 0 }}>No Stock Status loaded.</span>;
  if (own === null) return <span className="run-sub" style={{ marginTop: 0 }}>Not on the Stock Status here.</span>;
  return <div className="run-stock"><NeedSum par={own.par} onHand={own.onHand} onOrder={own.onOrder} /></div>;
}

/* A vendor's figures, in its own heading row right above its lines, so a change
   to a line shows its effect where he is looking. */
function VendorFigures({ v }) {
  const value = v.orderLines ? v.ordered : v.wanted;
  const short = v.min > 0 && v.gap > 0 && (v.orderLines || v.wanted > 0) && v.mode !== 'force_order' && v.mode !== 'uncond_transfer';
  return (
    <>
      <span className="run-vendor-fig">
        {v.orderLines ? <>Ordering <b>{money(v.ordered)}</b></> : value ? <>Lines come to <b>{money(value)}</b>, not ordered</> : 'Nothing to order'}
        {v.min ? <> of a <b>{money(v.min)}</b> minimum</> : ', no minimum'}
      </span>
      {short && <Pill tone="gold">{money(v.gap) + ' short'}</Pill>}
      {v.min > 0 && v.orderLines > 0 && v.meets && <Pill tone="app">Meets the minimum</Pill>}
      {v.mode === 'force_order' && <span className="run-vendor-mode">Always ordered</span>}
      {v.mode === 'uncond_transfer' && <span className="run-vendor-mode">Always from Austin</span>}
    </>
  );
}

/*
  **Pick the Account Edge item**, under a line with none, his word of 12 October
  2026 ("on the no vendor yet i need to be able to pick an account edge item").
  Searches the newest Account Edge list from the item being bought; Account Edge
  items already on an OnHand item come first, because those are the ones the
  line's ClaimSoft number can be saved onto (runPick.js says why).
*/
function AePick({ p, userId, onDone, onClose }) {
  const toast = useToast();
  const [text, setText] = useState(p.item);
  const [found, setFound] = useState(null);
  const [failed, setFailed] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let alive = true;
    setFound(null);
    const t = setTimeout(async () => {
      const r = await findAccountEdgeCandidates({ text, prefer: [p.item] });
      if (!alive) return;
      setFailed(r.error ? r.error.message : null);
      const exact = x => (keyOf(x.number) === keyOf(p.item) ? 0 : 1);
      setFound([...(r.items || [])].sort((a, b) => (b.taken - a.taken) || exact(a) - exact(b)));
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [text, p.item]);

  const pick = async x => {
    setBusy(x.number);
    const r = await linkClaimSoftToAe({ csNumber: p.item, aeNumber: x.number, userId });
    setBusy(null);
    if (!r.ok) { toast.error(r.error.message); return; }
    toast.success('ClaimSoft ' + p.item + ' is now on the item with Account Edge ' + x.number);
    onDone();
  };

  return (
    <div className="run-pick">
      <div className="run-pick-head">
        <b>Which Account Edge item is ClaimSoft <span className="rpnum">{p.item}</span>?</b>
        <button type="button" className="btn btn-quiet btn-sm" onClick={onClose}>Close</button>
      </div>
      <input className="fld" style={{ width: '100%' }} value={text} autoFocus
        aria-label="Search the Account Edge list" placeholder="An Account Edge item number, vendor item number or name"
        onChange={e => setText(e.target.value)} />
      {failed && <span className="run-warn">OnHand could not search the Account Edge list just now: {failed}</span>}
      {found === null ? <span className="run-sub">Looking...</span>
        : found.length === 0 ? <span className="run-sub">Nothing in the Account Edge list matches that. Try its number or a word from its name.</span>
        : (
          <div className="run-pick-list">
            {found.map(x => (
              <button key={x.number} type="button" className="btn btn-quiet run-pick-row" disabled={!x.taken || !!busy}
                title={x.taken ? undefined : 'Not on any OnHand item yet'} onClick={() => pick(x)}>
                <span><b className="rpnum">{x.number}</b> {x.name}
                  <span className="run-sub">{[x.vendor, x.vendorItem ? 'vendor item ' + x.vendorItem : null, x.unit, x.taken ? null : 'not on any OnHand item yet'].filter(Boolean).join(', ')}</span>
                </span>
                {busy === x.number && <span className="run-sub" style={{ marginTop: 0 }}>Saving...</span>}
              </button>
            ))}
          </div>
        )}
    </div>
  );
}

export default function RunPanel({ job, lines, userId, onSaved, onFinished }) {
  const toast = useToast();
  const [inputs, setInputs] = useState(null);
  const [failed, setFailed] = useState(null);
  const [overrides, setOverrides] = useState(() => overridesFrom(lines));
  const [saving, setSaving] = useState(false);
  const [picking, setPicking] = useState(null);

  const read = useCallback(async () => {
    const r = await loadRunInputs(job, lines);
    if (r.error) { setFailed(r.error.message); return; }
    setFailed(null);
    setInputs(r);
  }, [job, lines]);
  useEffect(() => { read(); }, [read]);

  const plan = useMemo(() => {
    if (!inputs) return null;
    return planRun({ ...inputs, lines, overrides });
  }, [inputs, lines, overrides]);

  const set = (id, field, value) => setOverrides(m => {
    const next = new Map(m);
    const o = { ...(next.get(id) || {}) };
    if (value === '' || value == null) delete o[field]; else o[field] = value;
    if (Object.keys(o).length) next.set(id, o); else next.delete(id);
    return next;
  });

  const save = async () => {
    setSaving(true);
    const r = await saveRun({ job, plan, reports: inputs.reports, userId });
    setSaving(false);
    if (!r.ok) { toast.error(r.error.message); return; }
    toast.success('Run saved on ' + job.reference);
    onSaved && onSaved();
  };

  if (failed) return <p style={{ color: 'var(--critical-ink)' }}>The run could not be worked out just now: {failed} <button className="btn btn-quiet" onClick={read}>Try again</button></p>;
  if (!plan) return <p style={{ color: 'var(--text-secondary)' }}>Working out the run...</p>;

  const r = inputs.reports;
  const today = new Date().toDateString();
  const stale = k => !r[k] || new Date(r[k].loaded_at).toDateString() !== today;
  const count = k => plan.lines.filter(p => p.route === k).length;
  const held = plan.lines.filter(p => p.route === 'hold');
  /* The run on screen against the run as saved: the file is made from the
     saved one, so any difference is said. */
  const saved = new Map(lines.map(l => [l.id, l]));
  const unsaved = !job.processed_at || plan.lines.some(p => {
    const l = saved.get(p.id);
    return !l || l.route !== p.route || Number(l.final_quantity) !== Number(p.qty) || keyOf(l.final_item_number) !== keyOf(p.item);
  });

  /* One group per vendor, in vendor order, each line under its vendor; lines
     with no vendor yet last (his word of 12 October 2026: "sorted by vendor
     first and then within each vendor see the items listed for that vendor"). */
  const vendorById = new Map(plan.vendors.map(v => [v.vendor_id, v]));
  const groupMap = new Map();
  for (const p of plan.lines) {
    const key = p.vendor_id || 'none';
    if (!groupMap.has(key)) groupMap.set(key, { key, name: p.vendor || 'No vendor yet', v: vendorById.get(p.vendor_id) || null, lines: [] });
    groupMap.get(key).lines.push(p);
  }
  const groups = [...groupMap.values()].sort((a, b) => (a.key === 'none') - (b.key === 'none') || a.name.localeCompare(b.name));
  const lineById = new Map(lines.map(l => [l.id, l]));
  /* A single item's own Stock Status row at the office, for a line in no family. */
  const ownStock = p => (inputs.stock ? (inputs.stock.get(keyOf(p.given)) || null) : undefined);
  /* Austin's own row on the morning's Excess Calc, by the line's Account Edge
     number: null with no Excess Calc or no number, 'none' when the item is not
     on it (a row with nothing on hand is left out at upload). */
  const austinOf = p => (!inputs.austin || !p.ae_number ? null : (inputs.austin.get(keyOf(p.ae_number)) || 'none'));

  const staleNote = (stale('stock_status') || stale('excess_calc'))
    ? [stale('stock_status') && 'the Stock Status', stale('excess_calc') && 'the Excess Calc'].filter(Boolean).join(' and ')
      + (stale('stock_status') && stale('excess_calc') ? ' are' : ' is') + " not today's. Upload today's at the top of the queue and the run works itself out again."
    : null;

  /*
    **Drawn on the house pieces from 12 October 2026**, after he found the run
    cramped, hard to read and unlike the rest of OnHand: OnHand's number strip,
    two proper tables (vendors, then lines) with full-size boxes, and one blue
    button. Nothing about what the run decides changed.
  */
  return (
    <>
      <Section title="The run" note={job.processed_at ? 'Last saved ' + dayAndClock(job.processed_at) + '.' : 'Worked out from the morning reports and your rules. Not saved yet.'}>
        {staleNote && <div style={{ fontSize: 14, padding: '10px 14px', borderRadius: 12, background: 'var(--warning-bg)', color: 'var(--warning-ink)' }}>{staleNote}</div>}
        <Figures>
          <Figure n={count('order')} label="To order" />
          <Figure n={count('transfer')} label="From Austin" />
          <Figure n={count('skip')} label="Skipped" />
          <Figure n={count('hold')} label="Held for you" tone={count('hold') ? 'var(--accent-ink)' : undefined} />
        </Figures>
      </Section>

      <Section title="Lines, by vendor" note="Each vendor's total and minimum sit right above its lines and change as you go.">
        <div className="run-wrap"><div className="run-scroll">
          <table className="run-table">
            <colgroup>
              {/* Shares of the line rather than fixed widths, so the spare room is spread
                  evenly across it, his word of 12 October 2026; .run-table has a floor. */}
              {['3%', '21%', '9%', '15.5%', '21%', '6%', '16.5%', '8%'].map((w, i) => <col key={i} style={{ width: w }} />)}
            </colgroup>
            <thead>
              <tr>
                <th>#</th>
                <th>ClaimSoft asked</th>
                <th>Account Edge</th>
                <th>Austin</th>
                <th>Stock at {job.destination ? job.destination.onhand_name : 'this office'}</th>
                <th className="num">Qty</th>
                <th>What to do</th>
                <th className="num">Line total</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => (
                <React.Fragment key={g.key}>
                  <tr className="run-vendor">
                    <td colSpan={8}>
                      <div className="run-vendor-in">
                        <span className="run-vendor-name">{g.name}</span>
                        {g.v && <VendorFigures v={g.v} />}
                      </div>
                    </td>
                  </tr>
                  {g.lines.map(p => {
                    const src = lineById.get(p.id) || {};
                    /* The true need is drawn in the Stock column, so it is not
                       repeated as words under the item. */
                    /* Vendor-wide reasons (its minimum, always ordered, always from
                       Austin, no minimum) live once in the vendor's heading, his
                       word of 12 October 2026, not on every line. */
                    const v = p.vendor || '';
                    const reasons = p.reason.filter(r => !r.startsWith('True need')
                      && !(v && (r.startsWith(v + ' is ') || r.startsWith(v + ' reaches ') || r.startsWith(v + ' always ')))
                      && !r.startsWith('No minimum for '));
                    return (
                      <React.Fragment key={p.id}>
                      <tr>
                        <td className="run-num">{p.line_no}</td>
                        <td>
                          <span className="rpnum run-num">{p.given}</span> <span style={{ color: 'var(--text-secondary)' }}>x {p.askedQty}</span>
                          {src.description && <span className="run-sub">{src.description}</span>}
                          {keyOf(p.item) !== keyOf(p.given) && <span className="run-sub" style={{ color: 'var(--text-primary)', fontWeight: 700 }}>Buying {p.item} instead</span>}
                          {reasons.length > 0 && <span className="run-sub">{reasons.join('. ')}</span>}
                          {p.warnings.map(w => <span key={w} className="run-warn">{w}</span>)}
                        </td>
                        <td>
                          {p.ae_number ? <span className="rpnum">{p.ae_number}</span> : (
                            <>
                              <span className="run-warn" style={{ marginTop: 0 }}>None</span>
                              <button type="button" className="btn btn-quiet btn-sm" style={{ marginTop: 6 }}
                                aria-expanded={picking === p.id} onClick={() => setPicking(picking === p.id ? null : p.id)}>Pick one</button>
                            </>
                          )}
                        </td>
                        <td className="run-bottom"><AustinCell a={austinOf(p)} /></td>
                        <td className="run-bottom"><StockCell p={p} own={ownStock(p)} /></td>
                        <td>
                          <input className="run-in qty" inputMode="numeric" defaultValue={p.qty} key={p.id + ':' + p.qty}
                            aria-label={'Quantity on line ' + p.line_no}
                            onBlur={e => { const v = e.target.value.trim(); if (v !== String(p.qty)) set(p.id, 'qty', v === '' ? null : Math.max(0, Math.round(Number(v)) || 0)); }} />
                        </td>
                        <td>
                          <div className="run-seg" role="group" aria-label={'What to do with line ' + p.line_no}>
                            {['order', 'transfer', 'skip', 'hold'].map(k => (
                              <button key={k} type="button" className={k} aria-pressed={p.route === k}
                                onClick={() => p.route !== k && set(p.id, 'route', k)}>{ROUTE_WORD[k]}</button>
                            ))}
                          </div>
                          {p.changedByHand && (
                            <button type="button" className="run-link run-undo"
                              onClick={() => setOverrides(m => { const n = new Map(m); n.delete(p.id); return n; })}>Undo my changes</button>
                          )}
                        </td>
                        <td className="num">
                          {p.cost != null ? <span className="run-num">{money(p.cost * p.qty)}</span> : ''}
                          {p.cost != null && <span className="run-sub">{money(p.cost)} each</span>}
                        </td>
                      </tr>
                      {picking === p.id && !p.ae_number && (
                        <tr className="run-pickrow"><td colSpan={8}>
                          <AePick p={p} userId={userId} onClose={() => setPicking(null)} onDone={() => { setPicking(null); read(); }} />
                        </td></tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div></div>
        <div className="ua-actions" style={{ justifyContent: 'flex-end' }}>
          {held.length > 0 && <span style={{ marginRight: 'auto', color: 'var(--accent-ink)', fontWeight: 700, fontSize: 14 }}>{held.length} line{held.length === 1 ? ' is' : 's are'} held for you to decide.</span>}
          <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save the run'}</button>
        </div>
      </Section>

      <AeFileSection job={job} lines={lines} unsaved={unsaved} userId={userId} onFinished={onFinished} />
    </>
  );
}
